import './styles/event.css';

import React, { useEffect, useState, useRef } from "react";
import Menu from "@mui/material/Menu";
import {
  Table,
  TableProps,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Modal,
} from "antd";
import type { InputRef, TableColumnType } from "antd";
import Button from "@mui/material/Button";
import { Typography as Typographys, Button as Buttons } from "antd";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import EventController from "./Controller/event_controller.ts";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CheckboxIcon from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import { Tag, Select } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { Upload, notification } from "antd";
import { MdPublish } from "react-icons/md";
import type { FilterDropdownProps } from "antd/es/table/interface";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import "../Admin/css/index.css";
import { NumericFormat } from "react-number-format";
import TextField from "@mui/material/TextField";
import CustomSeparator from "../../layouts/AdminLayout/Header/Breadcrumbs.js";
import Typography from "@mui/material/Typography";
import Highlighter from "react-highlight-words";
import { createStyles } from "antd-style";
import { Link } from "react-router-dom";
import Link2 from "@mui/material/Link";
import placeholder from "../../images/placeholder.jpg";
import BlastedReport from "./pdf/event-blast-report.tsx";
import { BlobProvider } from "@react-pdf/renderer";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Checkbox } from "antd";

const pdf_png = require("./../../images/pdf.png");

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
          .ant-table-row-expand-icon-cell {
            width: 65px !important;
          }
        }
      }
    `,
  };
});

const { Paragraph } = Typographys;
const { TextArea } = Input;

type NotificationType = "success" | "info" | "warning" | "error";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface ExpandedDataType {
  key: string;
  event_feeID: string;
  member_typeID: string;
  label: string;
  early_bird_rate: number;
  normal_rate: number;
  latecomer_rate: number;
  eventID: string;
}

interface DataType {
  key: string;
  userID: string;
  eventID: string;
  event_name: string;
  event_desc: string;
  event_poster: string;
  event_file: string;
  event_venue: number;
  registration_start_date: string;
  registration_end_date: string;
  early_bird_start_date: string;
  early_bird_end_date: string;
  event_start_date: string;
  event_end_date: string;
  max_pax: number;
  fees: any;
  no_of_registered_members: number;
  external_registration_link: string;
  event_category_name: string;
  publish_status: any;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType:
    | "number"
    | "text"
    | "select"
    | "datepicker"
    | "poster"
    | "document"
    | "publish_status"
  record: DataType;
  index: number;
  options: any;
}

interface EditableCellPropsSub extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "text" | "readonly" | "number";
  record: ExpandedDataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options, // Passing the options for the select
  ...restProps
}: any) => {
  let inputNode;

  if (inputType === "select") {
    inputNode = (
      <Select>
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else if (inputType === "datepicker") {
    inputNode = <Input type="date" />;
  } else if (inputType === "number") {
    inputNode = <InputNumber />;
  } else if (inputType === "poster") {
    // File upload input
    inputNode = (
      <Upload beforeUpload={() => false} listType="picture">
        <Buttons color="default" variant="solid" icon={<UploadOutlined />}>
          Choose...
        </Buttons>
      </Upload>
    );
  } else if (inputType === "publish_status") {
    inputNode = (
      <Select value={record.publish_status}>
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else {
    inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;
  }

  // File handler function
  const getFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList; // Return file list
  };

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[{ required: true, message: `Please input ${title}!` }]}
          valuePropName={inputType === "poster" ? dataIndex : "value"} // Set valuePropName conditionally
          getValueFromEvent={inputType === "poster" ? getFile : undefined} // Set getValueFromEvent conditionally
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const EditableCellSubCol: React.FC<
  React.PropsWithChildren<EditableCellPropsSub>
> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <NumericFormat
        displayType="input" // Make it editable
        thousandSeparator={true}
        prefix="RM"
        decimalScale={2} // Limit to 2 decimal places
        fixedDecimalScale={true} // Ensure 2 decimal places (e.g., 123.50)
        customInput={TextField} // Use Material-UI TextField for input
        variant="outlined" // Material-UI variant for styling
        fullWidth // Make the input take full width
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            fontSize: "12px",
            color: "black", // Set input text color
            borderRadius: "10px",
            "&:hover fieldset": {
              borderColor: "#999", // Border color on hover
            },
            backgroundColor: "white", // Set background color
          },
        }}
      />
    ) : (
      <Input />
    );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ baseColumns, checkedColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: Number) => () => {
    const currentIndex = checkedColumns.indexOf(value);
    const newChecked = [...checkedColumns];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked); // Call the parent onChange handler
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // maxHeight: 400,
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {baseColumns.map((value) => {
            const isChecked = checkedColumns.includes(value.dataIndex);

            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <CheckboxIcon
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

function Event() {
  // SUB COLUMN
  // __________________________________________________________________________________________________________________
  const [editingKeySubCol, setEditingKeySubCol] = useState("");

  const isEditingSubCol = (record: ExpandedDataType) => {
    return String(record.key) === String(editingKeySubCol);
  };

  const editSubCol = (
    record: Partial<ExpandedDataType> & { event_feeID: string }
  ) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });

    if (record?.key) {
      setEditingKeySubCol(record.key);
    }
  };

  const cancelEditSubCol = () => {
    setEditingKeySubCol("");
  };

  const [shouldRefetchData, setShouldRefetchData] = useState(false);

  const saveSubRow = async (key: React.Key, eventID: string) => {
    try {
      const row = (await form.validateFields()) as ExpandedDataType;
      console.log('checkRowData: ' + JSON.stringify(row));

      const payload = {
        event_feeID: key,
        eventID: eventID,
        data: row,
      };

      EventController.saveRowSubCol(payload).then((response) => {
        if (response?.status === "success") {
          openNotificationWithIcon(
            "success",
            "Your data has been saved successfully!",
            "Success"
          );
          setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
        }
      });
      setEditingKeySubCol("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
      errInfo.errorFields.forEach((item) => {
        openNotificationWithIcon("error", item.errors, "Error");
      });
    }
  };

  let MYRCurrency = new Intl.NumberFormat("ms-MY", {
    style: "currency",
    currency: "MYR",
  });

  const expandColumns = [
    { title: "Member Type", dataIndex: "member_typeID", key: "member_typeID" },

    {
      title: "Early Bird Rate",
      dataIndex: "early_bird_rate",
      key: "early_bird_rate",
      editable: true,
      render: (data: any) => {
        return MYRCurrency.format(data);
      },
    },
    {
      title: "Normal Rate",
      dataIndex: "normal_rate",
      key: "normal_rate",
      editable: true,
      render: (data: any) => {
        return MYRCurrency.format(data);
      },
    },
    {
      title: "Late Comer Rate",
      dataIndex: "latecomer_rate",
      key: "latecomer_rate",
      editable: true,
      render: (data: any) => {
        return MYRCurrency.format(data);
      },
    },
    {
      title: "Late Comer Rate",
      dataIndex: "latecomer_rate",
      key: "latecomer_rate",
      editable: true,
      render: (data: any) => {
        return MYRCurrency.format(data);
      },
    },
    {
      title: "Action",
      key: "operation",
      render: (_: any, record: ExpandedDataType) => {
        const editable = isEditingSubCol(record);
        return (
          <>
            {editable ? (
              <Space>
                <Buttons
                  onClick={() => saveSubRow(record.event_feeID, record.eventID)}
                  color="default"
                  variant="solid"
                >
                  Save
                </Buttons>
                <Buttons color="default" variant="outlined">
                  <Popconfirm
                    title="Sure to cancel?"
                    onConfirm={cancelEditSubCol}
                  >
                    Cancel
                  </Popconfirm>
                </Buttons>
              </Space>
            ) : (
              <Buttons
                color="default"
                variant="solid"
                icon={<BorderColorIcon />}
                disabled={editingKey !== "" || editingKeySubCol !== ""}
                onClick={() => editSubCol(record)}
              >
                Edit
              </Buttons>
            )}
          </>
        );
      },
    },
  ];

  const expandedRowRender = (record: DataType) => {
    const eventID = String(record.eventID);
    const matchingRow = subRows[eventID];

    if (matchingRow) {
      const expandDataSource = matchingRow.map<ExpandedDataType>((_, i) => {
        return {
          key: i.toString(),
          event_feeID: _.event_feeID,
          member_typeID: _.label,
          early_bird_rate: _.early_bird_rate,
          normal_rate: _.normal_rate,
          latecomer_rate: _.latecomer_rate,
          eventID: eventID,
        };
      });

      const mergedExpandColumns = expandColumns.map((col) => {
        if (!col.editable) {
          return col;
        }

        return {
          ...col,
          onCell: (record: ExpandedDataType) => ({
            record,
            inputType:
              col.dataIndex === "early_bird_rate" ||
              col.dataIndex === "normal_rate" ||
              col.dataIndex === "latecomer_rate"
                ? "number"
                : "text",
            dataIndex: col.dataIndex,
            title: col.title,
            editing: isEditingSubCol(record),
          }),
        };
      });

      return (
        <Form form={form} component={false}>
          <Table<ExpandedDataType>
            components={{
              body: { cell: EditableCellSubCol },
            }}
            columns={mergedExpandColumns}
            dataSource={expandDataSource}
            pagination={false}
          />
        </Form>
      );
    } else {
      return "No data found";
    }
  };

  // END OF SUB COLUMN
  // __________________________________________________________________________________________________________________

  const [api, contextHolder] = notification.useNotification();
  const userID = localStorage.getItem("userID");

  const openNotificationWithIcon = (
    type: NotificationType,
    desc: string,
    message: String
  ) => {
    api[type]({
      message: message,
      description: message,
    });
  };
  const [rows, setRows] = useState<any[]>([]);
  const [subRows, setSubRows] = useState<any[]>([]);

  const [data, setData] = useState<DataType[]>(rows);
  const [eventCategoryFilter, setEventCategoryFilter] = useState([]);

  //added by delon on 2025-12-04
  const [membersOnly, SetMembersOnly] = useState<any>({} as any); //{eventID: members_only}
  const membersOnlyRef = useRef<any>({} as any);
  const preMembersOnlyRef = useRef<any>({} as any);
  //watchers
  useEffect(() => {
    membersOnlyRef.current = {} as any;
    rows.map((row: any) => {
      membersOnlyRef.current[row.eventID] = (row.members_only == '1' ? true : false);
    });

    preMembersOnlyRef.current = {...membersOnlyRef.current};
    SetMembersOnly({...membersOnlyRef.current});
  }, [rows]);

  useEffect(() => {
    EventController.getRows().then((response) => {
      console.log(response);
      if (Array.isArray(response)) {
        const dynamicRows = response.map((item, index) => {
          const totalBeneficiaries = item.event_registration
            ? item.event_registration.reduce((sum, item_er) => {
                return sum + (item_er.beneficiary?.length || 0);
              }, 0) // Initial value for the accumulator
            : 0;

          return {
            key: index + 1,
            eventID: item.eventID,
            event_name: item.event_name,
            event_desc: item.event_desc,
            event_poster: item.event_poster,
            event_file: item.event_file,
            event_venue: item.event_venue,
            event_categoryID: item.event_categoryID,
            registration_start_date: item.registration_start_date,
            registration_end_date: item.registration_end_date,
            early_bird_start_date: item.early_bird_start_date,
            early_bird_end_date: item.early_bird_end_date,
            early_bird_price: item.early_bird_price,
            event_start_date: item.event_start_date,
            event_end_date: item.event_end_date,
            max_pax: item.max_pax,
            publish_status: item.publish_status,
            members_only: item.members_only,
            external_registration_link: item.external_registration_link,
            no_of_registered_members: totalBeneficiaries,
            event_category_name: item.event_category_name,
          };
        });

        const allSubRows = response
          .filter((item) => item.fees && item.fees.length > 0)
          .reduce((acc, item) => {
            item.fees.forEach((fee) => {
              if (!acc[fee.eventID]) {
                acc[fee.eventID] = [];
              }

              acc[fee.eventID].push(fee);
            });
            return acc;
          }, {});

        setSubRows(allSubRows);

        setRows(Array.from(dynamicRows));
      } else {
      }
    });

    EventController.getEventCategories().then((response) => {
      const filters = response?.map((item) => ({
        text: item.event_category_name,
        value: item.event_category_name,
      }));
      setEventCategoryFilter(filters);
    });
  }, [shouldRefetchData]);
  // Update data whenever rows changes

  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => {
    return record.eventID === editingKey;
  };
  const edit = (record: Partial<DataType> & { eventID: string }) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(String(record.eventID));
  };

  const cancel = () => {
    setEditingKey("");
    membersOnlyRef.current = {...preMembersOnlyRef.current};
    SetMembersOnly({...membersOnlyRef.current});
  };

  const delete_record = (rowId: string, userID) => {
    const payload = new FormData();
    payload.append("ID", rowId); // Append event ID
    payload.append("userID", userID); // Append event ID

    // console.log(userID);

    EventController.deleteEventRow(payload).then((response) => {
      console.log(response);
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been deleted successfully!",
          "Success"
        );
        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    });
  };

  const publish = (rowId: string, userID) => {
    const payload = new FormData();
    payload.append("ID", rowId); // Append event ID
    payload.append("userID", userID); // Append event ID

    // console.log(userID);

    EventController.publishEventRow(payload).then((response) => {
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Event has been published successfully!",
          "Success"
        );
        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    });
  };

  const save = async (key: any, userID) => {
    try {
      const row = (await form.validateFields()) as DataType;
      const newData = [...data];
      // Create FormData object
      const payload = new FormData();
      payload.append("ID", key); // Append event ID
      payload.append("userID", userID); // Append event ID

      console.log(key);
      console.log(userID);

      // Remove the second loop
      Object.keys(row).forEach((field) => {
        if (field !== "event_poster" && field !== "event_file") {
          payload.append(`data[${field}]`, row[field]); // Add form fields to payload
        }
      });

      // Append the file (if uploaded)
      if (row.event_poster) {
        const file = row.event_poster[0]["originFileObj"];
        payload.append("event_poster", file);
      }

      if (row.event_file) {
        const attachment = row.event_file[0]["originFileObj"];
        payload.append("event_file", attachment);
      }

      //add members only to payload
      payload.append('members_only', (membersOnlyRef.current[key] ? '1' : '0'));
      preMembersOnlyRef.current = {...membersOnlyRef.current};

      // Send the FormData payload using your EventController
      EventController.saveRow(payload).then((response) => {
        if (response?.status === "success") {
          openNotificationWithIcon(
            "success",
            "Your data has been saved successfully!",
            "Success"
          );

          setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
        }
      });

      setEditingKey(""); // Clear editing state
      // } else {
      //   newData.push(row); // Add new row to data
      //   setData(newData);
      //   setEditingKey(""); // Clear editing state
      // }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo); // Handle validation errors
    }
  };

  // State to track which rows are expanded
  const [expandedRows, setExpandedRows] = useState({});
  // Function to handle expanding/collapsing of a specific row
  const handleExpand = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key], // Toggle the expanded state for the specific row
    }));
  };

  const handleQueryStringInfoPage = (
    path: string,
    state: Record<string, string>
  ) => {
    const queryString = new URLSearchParams(state).toString();
    return `${path}?${queryString}`;
  };

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false,
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<{
    eventID: string;
    eventName: string;
  } | null>(null);
  const [blastDates, setBlastDates] = useState<string[]>([]); // Array to store blast dates
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const showModal = (eventID: string, eventName: string) => {
    console.log(eventID); // Now you can safely use eventID
    setSelectedEvent({ eventID, eventName }); // Store eventID and eventName
    setIsModalOpen(true);

    // Fetch data from the API
    EventController.getEventBlastDate(eventID)
      .then((response) => {
        console.log("API Response:", response);

        // Extract and format unique dates
        if (Array.isArray(response)) {
          const dates = response.map(
            (item) =>
              new Date(item.datetime_created).toLocaleDateString("en-GB") // Format date as DD-MM-YYYY
          );
          const uniqueDates = Array.from(new Set(dates)); // Remove duplicates
          setBlastDates(uniqueDates); // Set unique, formatted dates
        }
      })
      .catch((error) => {
        console.error("Error fetching blast dates:", error);
      });
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedDate("");
  };

  const ConvertUrlToFinalUrl = (url?: string) => {
    if (!url) return ""; // Return an empty string or handle it as needed

    let res = url.trim(); // Ensure there are no leading/trailing spaces

    if (!res.includes("www.") && !res.includes("localhost")) {
      res = res.replace(/^http:\/\//, "http://www.");
      res = res.replace(/^https:\/\//, "https://www.");
    }

    return res;
  };

  // const handleDownload = (data: string) => {
  //   if (!data) {
  //     console.error("No file URL provided.");
  //     return;
  //   }

  //   const fileName = data.split("/").pop() || "document.pdf";

  //   console.log("Downloading:", fileName);

  //   fetch(data)
  //     .then((response) => {
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch the file.");
  //       }
  //       return response.blob();
  //     })
  //     .then((blob) => {
  //       const fileURL = window.URL.createObjectURL(blob);
  //       console.log("File URL:", fileURL);

  //       // Create a download link
  //       const link = document.createElement("a");
  //       link.href = fileURL;
  //       link.download = fileName; // Set the correct file name

  //       // Append to the DOM, trigger download, and clean up
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);

  //       window.URL.revokeObjectURL(fileURL); // Free memory
  //     })
  //     .catch((error) => {
  //       console.error("Error downloading the file:", error);
  //     });
  // };
  const handleDownload = (data: string) => {
    if (!data) {
      console.error("No file URL provided.");
      return;
    }

    const link = document.createElement("a");
    link.href = data;
    link.target = "_blank"; // Open in new tab
    link.download = data.split("/").pop() || "document.pdf"; // Force download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate(event.target.value);
  };

  // Handle export (open PDF in a new tab)
  const handleExport = (url) => {
    console.log(url);
    if (url) {
      window.open(url, "_blank");
    }
  };

  const baseColumns = [
    {
      title: "No.",
      dataIndex: "key",
      key: "key",
      width: "5%",
    },
    {
      title: "ID",
      dataIndex: "eventID",
      key: "eventID",
    },
    {
      title: "Status",
      dataIndex: "publish_status",
      key: "publish_status",
      editable: true,
      render: (status: any) => {
        if (status === 1) {
          return <Tag color="green">{"Published"}</Tag>;
        } else {
          return <Tag color="red">{"Unpublished"}</Tag>;
        }
      },
      filters: [
        { text: "Published", value: 1 },
        { text: "Unpublished", value: 0 },
      ],
      onFilter: (value, record) => record.publish_status === value,
    },
    {
      title: "Event Name",
      dataIndex: "event_name",
      key: "event_name",
      width: 200,
      editable: true,
      ...getColumnSearchProps("event_name"),
      render: (text: any) => (
        <div style={{ whiteSpace: "normal" }}>
          {/* This allows for multi-line text */}
          {text}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "event_category_name",
      key: "event_category_name",
      width: 200,
      editable: false,
      filters: eventCategoryFilter,
      onFilter: (value, record) => record.event_category_name === value,
      render: (text: any) => (
        <div style={{ whiteSpace: "normal" }}>
          {/* This allows for multi-line text */}
          {text}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "event_desc",
      key: "event_desc",
      editable: true,
      render: (text: any, record: any) => {
        const isExpanded = expandedRows[record.key];
        return (
          <>
            <div style={{ whiteSpace: "normal", maxWidth: "200px" }}>
              {/* Set a maxWidth to manage the layout */}
              <Paragraph
                ellipsis={{
                  rows: 2, // Number of rows before ellipsis
                  expandable: false, // Disable AntD expand feature
                  expanded: isExpanded, // Use custom state to control expansion
                }}
                copyable // Enable copying of text
              >
                {text}
              </Paragraph>
              <Buttons type="link" onClick={() => handleExpand(record.key)}>
                {isExpanded ? "Less" : "More"}
              </Buttons>
            </div>
          </>
        );
      },
    },
    {
      title: "Event Poster",
      dataIndex: "event_poster",
      key: "event_poster",
      editable: true,
      render: (text: any) => {
        return (
          <>
            {text && text.trim() ? (
              <img
                src={text}
                alt="Event Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            ) : (
              <img
                src={placeholder}
                alt="Event Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            )}
          </>
        );
      },
    },
    {
      title: "Event File",
      dataIndex: "event_file",
      key: "event_file",
      editable: true,
      render: (data: string, record: DataType) => {
        const fileName = ConvertUrlToFinalUrl(data).split("/").pop();
        const fileExtension = fileName?.split(".").pop(); // Extracts the extension
        console.log(data);

        return (
          <>
            {data ? (
              <>
                {fileExtension === "pdf" && (
                  <img
                    src={pdf_png}
                    alt="PDF Icon"
                    style={{ width: "40px", marginRight: "8px" }}
                  />
                )}
                <Link2
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(ConvertUrlToFinalUrl(data));
                  }}
                  underline="none"
                >
                  <VisibilityIcon />
                </Link2>
              </>
            ) : (
              <span>No event file</span>
            )}
          </>
        );
      },
    },
    {
      title: "Venue",
      dataIndex: "event_venue",
      key: "event_venue",
      editable: true,
      width: 150,
      ...getColumnSearchProps("event_venue"),
      render: (text: string) => (
        <div style={{ whiteSpace: "normal" }}>
          {/* This allows for multi-line text */}
          {text}
        </div>
      ),
      // Optionally set a width to control the overall column width
    },

    {
      title: "Registration Start Date",
      dataIndex: "registration_start_date",
      key: "registration_start_date",
      editable: true,
    },
    {
      title: "Registration End Date",
      dataIndex: "registration_end_date",
      key: "registration_start_date",
      editable: true,
    },

    {
      title: "Start Date Time",
      dataIndex: "event_start_date",
      key: "event_start_date",
      editable: true,
    },
    {
      title: "End Date Time",
      dataIndex: "event_end_date",
      key: "event_end_date",
      editable: true,
    },
    // {
    //   title: "Early Bird Start Date",
    //   dataIndex: "early_bird_start_date",
    //   key: "early_bird_start_date",
    //   editable: true,
    // },
    // {
    //   title: "Early Bird End Date",
    //   dataIndex: "early_bird_end_date",
    //   key: "early_bird_end_date",
    //   editable: true,
    // },
    {
      title: "Maximum Pax",
      dataIndex: "max_pax",
      key: "max_pax",
      width: 100,
      editable: true,
      ...getColumnSearchProps("max_pax"),
    },
    {
      title: "Registered Members",
      dataIndex: "no_of_registered_members",
      key: "no_of_registered_members",
      width: 120,
      editable: false,
    },
    {
      title: "Registration Form Link",
      dataIndex: "link",
      key: "link",
      width: 180,
      editable: false,

      render: (_: any, record: any) => {
        let link = '';
        if(
          record.event_categoryID != '7'
          && record.event_categoryID != '16'
          && record.event_categoryID != '19'
          && record.event_categoryID != '20'
        ) {
          link = `https://www.ppkm.org.my/ppkm/join_event?eventID=${record.eventID}`;
        } else {
          link = '';
        }

        console.log('checkEventCategoryID:  ' + record.event_categoryID);
  
        const isExpanded = true;
        return (
          <>
            <Paragraph
              ellipsis={{
                rows: 2, // Number of rows before ellipsis
                expandable: false, // Disable AntD expand feature
                expanded: isExpanded, // Use custom state to control expansion
              }}
              copyable // Enable copying of text
            >
              <a href={link} target="_blank" rel="noopener noreferrer">
                {link}
              </a>
            </Paragraph>
          </>
        );
      },
    },
    {
      title: "Members Only",
      dataIndex: "members_only",
      key: "members_only",
      width: 100,
      render: (_: any, record: any, index: number) => {
        const editable = isEditing(record);

        return (
          <Checkbox
            checked={membersOnlyRef.current[record.eventID]}
            onChange={(e) => {
              membersOnlyRef.current[record.eventID] = e.target.checked;
              console.log('checked: ' + membersOnlyRef.current[record.eventID]);
              SetMembersOnly({...membersOnlyRef.current});
            }}
            disabled={editable ? false : true}
          />
        );
      }
    },
    {
      title: "External Registration Link",
      dataIndex: "external_registration_link",
      key: "external_registration_link",
      editable: true,
      width: 150,
      ...getColumnSearchProps("external_registration_link"),
      render: (text: string) => (
        <div style={{ whiteSpace: "normal" }}>
          {/* This allows for multi-line text */}
          {text}
        </div>
      ),
      // Optionally set a width to control the overall column width
    },
    {
      title: "operation",
      dataIndex: "operation",
      width: 150,
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);

        const pathInfo = "/admin/event_info";
        const stateInfo = { eventID: record.eventID };
        const fullPathInfo = handleQueryStringInfoPage(pathInfo, stateInfo);

        return editable ? (
          <span>
            <Space>
              <Buttons
                onClick={() => save(record.eventID, userID)}
                // style={{ marginInlineEnd: 8 }}
                color="default"
                variant="solid"
              >
                Save
              </Buttons>
              <Buttons color="default" variant="outlined">
                <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                  Cancel
                </Popconfirm>
              </Buttons>
            </Space>
          </span>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Buttons
                color="default"
                variant="solid"
                icon={<EditOutlined />}
                disabled={editingKey !== ""}
                onClick={() => edit(record)}
                style={{ marginTop: 10, marginBottom: 7 }}
              >
                Edit
              </Buttons>
              {record.publish_status === 0 && (
                <Buttons
                  disabled={editingKey !== ""}
                  icon={<MdPublish />}
                  style={{ marginBottom: 7 }}
                >
                  <Popconfirm
                    title="Sure to publish?"
                    onConfirm={() => publish(record.eventID, userID)}
                  >
                    Publish
                  </Popconfirm>
                </Buttons>
              )}
              <Buttons
                type="primary"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey !== ""}
                style={{ marginBottom: 7 }}
              >
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => delete_record(record.eventID, userID)}
                >
                  Delete
                </Popconfirm>
              </Buttons>
              <Buttons
                type="default"
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 7 }}
              >
                <Link
                  to={fullPathInfo}
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  Info
                </Link>
              </Buttons>

              {/* <Buttons
                type="default"
                icon={<FilePdfOutlined />}
                onClick={() => showModal(record.eventID, record.event_name)}
              >
                Blasted Report
              </Buttons> */}
              <Modal
                title={`Blasted Report for ${
                  selectedEvent?.eventName || "Event"
                }`}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                mask={false}
                style={{
                  boxShadow: "none", // Remove shadow
                  borderRadius: "10px", // Optional customization
                }}
                footer={(_, { OkBtn, CancelBtn }) => (
                  <>
                    {selectedEvent && selectedDate && (
                      <BlobProvider
                        document={
                          <BlastedReport
                            eventID={selectedEvent.eventID}
                            date={selectedDate} // Pass the selected date
                          />
                        }
                      >
                        {({ blob, url, loading }) =>
                          loading ? (
                            "Generating PDF..."
                          ) : (
                            <Buttons
                              color="default"
                              variant="solid"
                              style={{
                                marginBottom: "10px",
                                textDecoration: "none",
                              }}
                              onClick={() => handleExport(url)}
                            >
                              Export
                            </Buttons>
                          )
                        }
                      </BlobProvider>
                    )}
                    <CancelBtn />
                  </>
                )}
                width={500}
              >
                <label
                  className="form-label"
                  style={{
                    fontSize: "15px",
                    fontWeight: "2000",
                    marginTop: "10px",
                  }}
                >
                  Select Blast Date:
                </label>
                <select
                  className="form-select mb-3"
                  onChange={handleDateChange} // Track date changes
                  value={selectedDate || ""}
                >
                  <option value="">Select a Date</option>
                  {blastDates.length > 0 ? (
                    blastDates.map((date, index) => (
                      <option key={index} value={date}>
                        {date} {/* Display formatted and unique dates */}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No Email Blast Yet
                    </option>
                  )}
                </select>
              </Modal>
            </div>
          </>
        );
      },
    },
  ];

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);

    // call api here
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const [checkedColumns, setCheckedColumns] = useState([
    "event_category_name",
    "publish_status",
    "max_pax",
    "event_name",
    "event_desc",
    "event_poster",
    "event_venue",
    "external_registration_link",
    "status",
    "no_of_registered_members",
    "link",
    "members_only",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  // Define your status options
  const statusOptions = [
    { label: "Publish", value: 1 },
    { label: "Unpublish", value: 0 },
    // Add more options as needed
  ];

  // Create mergedColumns directly using baseColumns
  const mergedColumns = baseColumns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => {
          // Ensure title and dataIndex are strictly strings
          const columnTitle =
            typeof col.title === "string" ? col.title : String(col.title);
          const columnDataIndex =
            typeof col.dataIndex === "string"
              ? col.dataIndex
              : String(col.dataIndex);

          return {
            record,
            inputType:
              columnDataIndex === "publish_status"
                ? "publish_status"
                : columnDataIndex === "registration_start_date" ||
                  columnDataIndex === "registration_end_date"
                ? "datepicker"
                : columnDataIndex === "event_poster"
                ? "poster"
                : columnDataIndex === "event_file"
                ? "poster"
                : "text",
            dataIndex: columnDataIndex,
            title: columnTitle,
            editing: isEditing(record),
            options:
              columnDataIndex === "publish_status" ? statusOptions : undefined,
          };
        },
      };
    });

  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "auto", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />

          {contextHolder}

          <CustomSeparator pageName="event" />

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Event Directory
          </Typography>

          <Stack
            spacing={2}
            direction="row"
            sx={{ mb: 3, justifyContent: "space-between" }}
          >
            <CheckboxList
              baseColumns={baseColumns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
            <Button
              variant="contained"
              style={{ backgroundColor: "#d33333" }}
              href="/ppkm/admin/event_blast"
            >
              <AttachEmailIcon sx={{ mr: 1 }} /> Email
            </Button>
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "200%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table
                className={`${styles.customTable} event-table`}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                columns={mergedColumns}
                // expandable={{
                //   expandedRowRender,
                //   defaultExpandedRowKeys: [""],
                // }}
                dataSource={rows}
                // rowSelection={rowSelection}
                rowKey="eventID" // Set your custom key here
                scroll={{ x: "max-content", y: 150 * 5 }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Event;
